import { useCallback, useEffect, useState } from 'react'
import { Topic } from '@/types'
import { PrivacyType, TopicType } from '@/utils/constants'
import { isTopicNotAuthorized, parseAjaxErrorMessage } from '@/utils/apiError'
import { createAnonymousApi } from '@/utils/anonymousApi'
import { ROOM_DEFAULTS } from '../constants'
import { StudioPin, StudioSceneState } from '../types'
import { convertLegacyClassicGallery } from '../utils/legacyClassicConverter'
import { parseSceneNote, parsePinNotes } from './useStudio3DData'

export type PublicGalleryStatus = 'loading' | 'ready' | 'private' | 'not-found'

export interface UsePublicGalleryResult {
  status: PublicGalleryStatus
  topic: Topic | null
  ownerName: string
  galleryTitle: string
  /** `pins` is always a real array here (merged notes + legacy blob) — never the optional-on-StudioSceneState kind. */
  scene: StudioSceneState & { pins: StudioPin[] }
}

const EMPTY_SCENE: StudioSceneState = { version: 1, room: ROOM_DEFAULTS, arts: [], pins: [] }
const anonymousApi = createAnonymousApi()

/**
 * Guest / public gallery fetch — no Cognito session required. Mirrors
 * `usePublicAlbum`'s anonymous-axios shape exactly (same `topicNoteList`
 * endpoint, same notes-reversal, no `userID` in the body) so it works for
 * signed-out visitors, then re-derives the same scene a signed-in owner
 * would see in Studio: a saved `studio3dSceneStateV1` note first, falling
 * back to the legacy Classic conversion for galleries that predate this
 * Studio. Unlike `useStudio3DData`, there is no source-album/auto-layout
 * fallback — a public guest has no business pulling in someone's private
 * album photos, and a shared link only ever makes sense once a real scene
 * (or legacy Classic room) has actually been saved.
 *
 * `status === 'private'` is a client-side courtesy, not the security
 * boundary — the backend's own visibility rules are what actually gate the
 * `topicNoteList` response. This just decides what this screen renders.
 */
export function usePublicGallery(shareId: string | undefined): UsePublicGalleryResult {
  const [status, setStatus] = useState<PublicGalleryStatus>('loading')
  const [topic, setTopic] = useState<Topic | null>(null)

  const refresh = useCallback(async () => {
    if (!shareId) {
      setStatus('not-found')
      return
    }
    setStatus('loading')
    try {
      const response = await anonymousApi.post('/ajax/topics', {
        requestType: 'topicNoteList',
        topicID: shareId,
        format: 'json',
        pageNo: 1,
      })
      if (response.data?.isError) {
        // The owner's `isAllowed()` rejection for a private topic — same
        // outcome as the visibility check below, just caught earlier
        // (the backend never sends topic data for it at all).
        if (isTopicNotAuthorized(response.data.errorMessage)) {
          setTopic(null)
          setStatus('private')
          return
        }
        throw new Error(parseAjaxErrorMessage(response.data.errorMessage, 'Gallery unavailable'))
      }

      const notes = response.data?.notes || []
      const nextTopic: Topic = {
        ...response.data,
        notes: [...notes].reverse(),
        topicID: response.data.topicID || shareId,
      }

      if (String(nextTopic.topicType) !== String(TopicType.GALLERY)) {
        setTopic(null)
        setStatus('not-found')
        return
      }

      setTopic(nextTopic)
      setStatus(nextTopic.visibility === PrivacyType.PRIVATE.toString() ? 'private' : 'ready')
    } catch {
      setTopic(null)
      setStatus('not-found')
    }
  }, [shareId])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Real pin notes (STUDIO_PIN_NOTE_TYPE) plus whatever's still embedded in
  // an older, pre-migration scene blob — same merge `useStudio3DData` does
  // for the owner's own session. See parsePinNotes for why pins live here
  // instead of inside the scene blob.
  const baseScene = parseSceneNote(topic) || convertLegacyClassicGallery(topic) || EMPTY_SCENE
  const scene: StudioSceneState & { pins: StudioPin[] } = {
    ...baseScene,
    pins: [...parsePinNotes(topic), ...(baseScene.pins || [])],
  }

  return {
    status,
    topic,
    ownerName: topic?.userName || 'Someone',
    galleryTitle: topic?.topicName || 'Untitled gallery',
    scene,
  }
}
